import React from "react";
import { useEdit } from "./InlineEdit";
import {
  BANNER_IMAGE_ZOOM_MAX,
  BANNER_IMAGE_ZOOM_MIN,
  bannerImageFrameStyle,
  imageFrameEditKey,
  normalizeBannerImageFrame,
  parseImageFrameEdit,
  serializeImageFrameEdit,
  type BannerImageFrame,
} from "./templateBannerUtils";
import {
  cropShapeClass,
  type ImageFrameShape,
} from "./websiteImageFrameAspect";

function pointInBox(
  box: DOMRect,
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  const width = box.width || 1;
  const height = box.height || 1;
  return {
    x: Math.round(Math.min(100, Math.max(0, ((clientX - box.left) / width) * 100))),
    y: Math.round(Math.min(100, Math.max(0, ((clientY - box.top) / height) * 100))),
  };
}

/**
 * Focal crop for website banners / photo slots. Grab (press) and drag the
 * thumbnail to choose the focal point, then zoom in.
 * Defaults to `cover` (avatars, banners, cards). Pass `contain` for letterboxed art.
 */
export function BannerImageFrameControl({
  src,
  zoom,
  x,
  y,
  aspectRatio,
  shape = "rounded",
  objectFit = "cover",
  uploading = false,
  onChange,
  onCommit,
}: {
  src: string;
  zoom?: number;
  x?: number;
  y?: number;
  /** CSS aspect-ratio for the crop thumb (match live banner frame). */
  aspectRatio?: string;
  /** Outer mask — `circle` for avatars, `rounded` for rect frames. */
  shape?: ImageFrameShape;
  /** Match live frame fit — banners use cover; letterboxed art uses contain. */
  objectFit?: "cover" | "contain";
  uploading?: boolean;
  onChange: (frame: BannerImageFrame) => void;
  /** Fired when the drag or slider settles, for the slower DB write. */
  onCommit?: (frame: BannerImageFrame) => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const dragging = React.useRef(false);
  const commitTimer = React.useRef<number | null>(null);
  const onCommitRef = React.useRef(onCommit);
  onCommitRef.current = onCommit;
  const frame = normalizeBannerImageFrame({ imageZoom: zoom, imageX: x, imageY: y });
  const frameRef = React.useRef(frame);
  frameRef.current = frame;
  const imgStyle = bannerImageFrameStyle(frame, { objectFit }) || {
    objectFit,
    objectPosition: "50% 50%",
  };
  const shapeMod = cropShapeClass(shape);

  const publish = React.useCallback(
    (next: BannerImageFrame, commitNow = false) => {
      const normalized = normalizeBannerImageFrame(next);
      onChange(normalized);
      if (!onCommit) return;
      if (commitTimer.current != null) window.clearTimeout(commitTimer.current);
      if (commitNow) {
        onCommit(normalized);
        return;
      }
      commitTimer.current = window.setTimeout(() => onCommit(normalized), 350);
    },
    [onChange, onCommit],
  );

  React.useEffect(
    () => () => {
      if (commitTimer.current == null) return;
      window.clearTimeout(commitTimer.current);
      onCommitRef.current?.(frameRef.current);
    },
    [],
  );

  const frameFromEvent = (e: React.PointerEvent, commitNow = false) => {
    if (!ref.current) return;
    const point = pointInBox(
      ref.current.getBoundingClientRect(),
      e.clientX,
      e.clientY,
    );
    publish({ zoom: frameRef.current.zoom, ...point }, commitNow);
  };

  return (
    <div className="mte-we-ban-crop-row">
      <div
        ref={ref}
        className={["mte-we-ban-crop", shapeMod].filter(Boolean).join(" ")}
        style={aspectRatio ? { aspectRatio } : undefined}
        onPointerDown={(e) => {
          if (uploading || e.button !== 0) return;
          // Keep pan inside the crop — don't bubble into parent sortable/DnD.
          e.preventDefault();
          e.stopPropagation();
          dragging.current = true;
          (e.currentTarget as HTMLElement).classList.add("is-dragging");
          e.currentTarget.setPointerCapture(e.pointerId);
          frameFromEvent(e);
        }}
        onPointerMove={(e) => {
          if (!dragging.current) return;
          e.stopPropagation();
          frameFromEvent(e);
        }}
        onPointerUp={(e) => {
          if (!dragging.current) return;
          e.stopPropagation();
          dragging.current = false;
          (e.currentTarget as HTMLElement).classList.remove("is-dragging");
          frameFromEvent(e, true);
        }}
        onPointerCancel={(e) => {
          e.stopPropagation();
          dragging.current = false;
          (e.currentTarget as HTMLElement).classList.remove("is-dragging");
        }}
      >
        <img src={src} alt="" style={imgStyle} draggable={false} />
        <span
          className="mte-we-ban-crop-dot"
          style={{ left: `${frame.x}%`, top: `${frame.y}%` }}
        />
        {uploading ? (
          <span className="mte-we-photo-busy" aria-hidden="true">
            <span className="mte-we-photo-spinner" />
          </span>
        ) : null}
      </div>
      <div className="mte-we-ban-crop-controls">
        <div className="mte-we-ban-crop-slider">
          <span className="mte-we-ban-crop-label">Zoom</span>
          <input
            type="range"
            min={BANNER_IMAGE_ZOOM_MIN}
            max={BANNER_IMAGE_ZOOM_MAX}
            step={0.05}
            value={frame.zoom}
            aria-label="Image zoom"
            disabled={uploading}
            onChange={(e) =>
              publish({ ...frame, zoom: Number(e.target.value) })
            }
            onPointerUp={(e) =>
              publish(
                { ...frame, zoom: Number((e.target as HTMLInputElement).value) },
                true,
              )
            }
          />
          <span className="mte-we-ban-crop-value">{frame.zoom.toFixed(1)}x</span>
        </div>
        <div className="mte-we-ban-crop-hint">Grab and drag to reposition</div>
      </div>
    </div>
  );
}

/** Focal point for any website-template image that already has an upload slot. */
export function SlotImageFrameControl({
  slot,
  src,
  aspectRatio,
  shape,
  objectFit,
  uploading,
  onDirty,
}: {
  slot: string;
  src: string;
  /** CSS aspect-ratio matching the live page frame. */
  aspectRatio?: string;
  shape?: ImageFrameShape;
  objectFit?: "cover" | "contain";
  uploading?: boolean;
  /** Enable inspector Update when the frame changes. */
  onDirty?: () => void;
}) {
  const { get, set } = useEdit();
  const key = imageFrameEditKey(slot);
  const saved = parseImageFrameEdit(get(key, ""));
  const frame = saved || normalizeBannerImageFrame(null);
  if (!String(src || "").trim()) return null;
  return (
    <BannerImageFrameControl
      src={src}
      aspectRatio={aspectRatio}
      shape={shape}
      objectFit={objectFit}
      uploading={uploading}
      zoom={frame.zoom}
      x={frame.x}
      y={frame.y}
      onChange={(next) => {
        set(key, serializeImageFrameEdit(next));
        onDirty?.();
      }}
    />
  );
}
